import React, { useState } from 'react';
import authService from '../../services/authService';

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    login: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await authService.login(formData);

      if (result.success) {
        onLoginSuccess(result.userInfo);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex overflow-hidden">
      {/* Left side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 xl:px-12 overflow-y-auto">
        <div className="w-full max-w-sm lg:max-w-md xl:max-w-lg py-8">
          <div>
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden">
                  <img
                    src="/assets/images/logo2.jpg"
                    alt="Général Sécurité Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to icon if image fails to load
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<i class="fas fa-fire-extinguisher text-white text-2xl"></i>';
                    }}
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                  <i className="fas fa-shield-alt text-white text-xs"></i>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                Général Sécurité
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Plateforme d'administration sécurisée
              </p>
            </div>
          </div>

          <div>
            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 animate-pulse">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <i className="fas fa-exclamation-triangle text-red-400"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Username Field */}
              <div>
                <label htmlFor="login" className="block text-sm font-semibold text-gray-900 mb-2">
                  Nom d'utilisateur
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-user text-gray-400 text-sm"></i>
                  </div>
                  <input
                    id="login"
                    name="login"
                    type="text"
                    autoComplete="username"
                    required
                    value={formData.login}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white focus:bg-white"
                    placeholder="Entrez votre nom d'utilisateur"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-lock text-gray-400 text-sm"></i>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-12 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white focus:bg-white"
                    placeholder="Entrez votre mot de passe"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400 hover:text-gray-600 transition-colors text-sm`}></i>
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative w-full flex justify-center py-4 px-4 border border-transparent text-sm font-semibold rounded-xl text-white transition-all duration-200 ${loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                    }`}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      <span>Connexion en cours...</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <i className="fas fa-sign-in-alt mr-3"></i>
                      <span>Se connecter</span>
                      <i className="fas fa-arrow-right ml-3 group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right side - Brand/Image Section */}
      <div className="hidden lg:block relative flex-1 max-w-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 -left-4 w-64 h-64 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
            <div className="absolute top-0 -right-4 w-64 h-64 bg-primary-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
            <div className="absolute -bottom-8 left-20 w-64 h-64 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
          </div>

          {/* Content */}
          <div className="relative h-full flex flex-col justify-center items-center text-white p-8 xl:p-12">
            <div className="text-center max-w-md">
              {/* Main Icon */}
              <div className="mb-6">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-white border-opacity-30 mx-auto mb-4">
                  <i className="fas fa-fire-extinguisher text-3xl"></i>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight">
                Sécurité Incendie
                <br />
                <span className="text-primary-200">Professionnelle</span>
              </h1>

              {/* Description */}
              <p className="text-lg xl:text-xl text-primary-100 mb-6 leading-relaxed">
                Gérez vos équipements de sécurité incendie avec notre plateforme administrative complète
              </p>

              {/* Features */}
              <div className="space-y-3 text-left">
                <div className="flex items-center text-primary-100">
                  <div className="w-7 h-7 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="fas fa-check text-xs"></i>
                  </div>
                  <span className="text-sm">Gestion des extincteurs</span>
                </div>
                <div className="flex items-center text-primary-100">
                  <div className="w-7 h-7 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="fas fa-check text-xs"></i>
                  </div>
                  <span className="text-sm">Suivi des interventions</span>
                </div>
                <div className="flex items-center text-primary-100">
                  <div className="w-7 h-7 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <i className="fas fa-check text-xs"></i>
                  </div>
                  <span className="text-sm">Certificats de conformité</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1200 120" className="w-full h-10 text-primary-800">
              <path d="M0,60 C300,100 900,20 1200,60 L1200,120 L0,120 Z" fill="currentColor" opacity="0.3"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;